import { useEffect, useRef } from "react";
import {
  bigDealAmount,
  catchUpMinOfflineMs,
  coinRainAmount,
  recordMinAmount,
  smallSaleConfetti,
} from "../constants";
import { byId } from "../animations/registry";
import { readRecord, writeRecord } from "../utils/saleRecord";
import { readLastSeen, writeLastSeen } from "../utils/lastSeen";

// Kijkt of er tussen twee keer ophalen iets is bijgekomen en vertaalt dat
// naar animaties. De eerste meting telt niet mee: dat is gewoon de stand
// zoals hij was, niet iets wat zojuist gebeurde.
//
// Behalve na een herstart: dan pakken we de stand van vlak voor de tv
// uitging (zie utils/lastSeen.js) en halen we in wat er sindsdien is
// verkocht. Zie useSaleEvents onderaan.

function snapshot(data) {
  const totals = new Map();
  const order = [];

  (data.rankList || []).forEach((user) => {
    if (!user?.name) return;
    totals.set(user.name, user.totalPrice || 0);
    order.push(user.name);
  });

  // Hetzelfde trucje voor de diensten: daaruit halen we later wát er
  // verkocht is, want de ranglijst weet alleen wie en voor hoeveel.
  const services = new Map();
  (data.soldServices || []).forEach((service) => {
    if (!service?.name) return;
    services.set(service.name, service.totalPrice || 0);
  });

  const target = data.teamTarget?.amount || 0;
  const percent = target
    ? Math.min(100, Math.round(((data.scoredTowardsTarget || 0) / target) * 100))
    : 0;

  return { totals, order, services, amount: data.amount || 0, percent };
}

// Van en naar localStorage: Maps kunnen niet in JSON, dus daar worden het
// gewone objecten. at is het moment van de meting, scope de maand.
function pack(snap, scope) {
  return {
    scope,
    at: snap.at,
    totals: Object.fromEntries(snap.totals),
    order: snap.order,
    services: Object.fromEntries(snap.services),
    amount: snap.amount,
    percent: snap.percent,
  };
}

function unpack(record) {
  return {
    totals: new Map(Object.entries(record.totals || {})),
    order: record.order || [],
    services: new Map(Object.entries(record.services || {})),
    amount: record.amount || 0,
    percent: record.percent || 0,
    at: record.at,
  };
}

// Welke diensten zijn er bij gekomen, en voor hoeveel. Grootste eerst, zodat
// die naast de grootste verkoop komt te liggen.
function soldServices(before, now) {
  const grown = [];
  now.services.forEach((price, name) => {
    const was = before.services.get(name) ?? 0;
    if (price > was) grown.push({ name, amount: price - was });
  });
  return grown.sort((a, b) => b.amount - a.amount);
}

// Zoekt bij een verkoop de dienst die er in dezelfde ronde voor hetzelfde
// bedrag bij is gekomen, en haalt hem uit de lijst: twee verkopen van
// hetzelfde bedrag pakken zo niet dezelfde dienst. Past niets precies, dan
// blijft de dienst leeg - liever geen regel dan de verkeerde dienst.
function takeService(grown, amount) {
  const index = grown.findIndex((service) => service.amount === amount);
  if (index < 0) return null;
  return grown.splice(index, 1)[0].name;
}

// Wie is erbij gekomen of heeft meer op de teller? Grootste deal eerst,
// zodat die de banner krijgt als er meerdere zijn. Met erbij wát er
// verkocht is: is er precies één verkoop en één dienst die opliep, dan
// hoort dat bij elkaar, ook als het bedrag over meerdere regels verdeeld
// staat.
function salesBetween(before, now) {
  const sales = [];
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    if (price > was) sales.push({ name, amount: price - was });
  });
  sales.sort((a, b) => b.amount - a.amount);

  const grown = soldServices(before, now);
  sales.forEach((sale) => {
    sale.service = takeService(grown, sale.amount);
  });
  if (sales.length === 1 && !sales[0].service && grown.length === 1) {
    sales[0].service = grown[0].name;
  }

  return sales;
}

// De grootste stijger in de ranglijst, of null.
function biggestClimber(before, now) {
  let climber = null;
  now.order.forEach((name, index) => {
    const was = before.order.indexOf(name);
    if (was < 0 || was <= index) return;
    const jump = was - index;
    if (!climber || jump > climber.jump) {
      climber = { name, position: index + 1, from: was + 1, jump };
    }
  });
  return climber;
}

// De volgorde van één verkoop, in ms ten opzichte van de balk. Eerst de
// aankondiging op donker beeld (of bij een record het vuurwerk), en pas als
// die weg is valt de balk bovenlangs. Kort daarna licht de rij op en begint
// het te regenen; het kaartje van de stijger komt midden in de regen. Zo
// staat er nooit meer dan één ding tegelijk in het midden van het beeld.
const flashAfterBar = 700; // de balk is dan geland
const rainAfterBar = 1200;
const gemsAfterCoins = 700;
const climbAfterRain = 1800;

// Alles wat bij één verkoop hoort, als lijst van [animatie, gegevens, tijd].
// De animatiemotor speelt het als één groepje af, zie useAnimations.
//
// record: het oude record als deze verkoop eroverheen gaat. Dan opent het
// vuurwerk met de recordkaart in plaats van de gewone aankondiging; twee
// donkere kaarten achter elkaar met dezelfde naam erop is er één te veel.
// climber: de stijger als dat deze verkoper is, zodat het kaartje bij de
// verkoop hoort in plaats van er los achteraan te komen.
function saleGroup(sale, { record = null, climber = null } = {}) {
  const opener = record
    ? [
        "fireworks",
        {
          name: sale.name,
          amount: sale.amount,
          service: sale.service,
          previous: record.amount,
        },
      ]
    : ["sale-splash", sale];
  const barAt = byId(opener[0]).ms;
  const rainAt = barAt + rainAfterBar;

  const events = [
    [...opener, 0],
    ["gauge-pulse", {}, barAt],
    ["sale-bar", sale, barAt],
    ["row-flash", { name: sale.name }, barAt + flashAfterBar],
  ];

  // Wat er valt hangt af van het bedrag: diamanten en munten bij een grote
  // deal, munten bij een middelgrote, en een lichte confetti bij de rest,
  // zodat je ook bij een kleine verkoop merkt dat er iets is verkocht.
  if (sale.amount >= bigDealAmount) {
    events.push(["coin-rain", {}, rainAt], ["gem-rain", {}, rainAt + gemsAfterCoins]);
  } else if (sale.amount >= coinRainAmount) {
    events.push(["coin-rain", {}, rainAt]);
  } else if (smallSaleConfetti > 0) {
    events.push(["confetti-burst", { count: smallSaleConfetti }, rainAt]);
  }

  if (climber) {
    const climbAt = rainAt + climbAfterRain;
    events.push(["rank-up", climber, climbAt]);
    if (climber.position <= 3) events.push(["podium-bounce", {}, climbAt]);
  }

  return events;
}

// Geeft terug wat er moet gebeuren, als lijst van groepjes die de
// animatiemotor na elkaar afspeelt. Elke verkoop is een eigen groepje: twee
// verkopen door elkaar heen leest niemand.
//
// record: de grootste losse verkoop tot nu toe, of null als we die (nog)
// niet kennen. Zonder record geen vuurwerk - de eerste verkoop die we zien
// is niet per se een record, we hebben alleen de geschiedenis niet.
//
// catchUp: inhalen na een herstart. De verkopen zelf staan dan al op de
// kaart "Verkopen gemist" (zie useSaleEvents), dus die krijgen geen eigen
// beurt; alleen de stijger en het target komen er nog achteraan. Geen
// vuurwerk en geen record: de API geeft totalen per verkoper, en wat na een
// nacht als één bedrag binnenkomt kunnen best drie losse verkopen zijn. En
// het target komt als afsluiter, niet in plaats van alles: eerst zien hoe
// het team er kwam, dan het feestje.
function toGroups(before, now, { record = null, catchUp = false } = {}) {
  const targetHit = now.percent >= 100 && before.percent < 100;
  const targetGroup = [
    ["target-hit", { amount: now.amount }],
    ["confetti-burst", {}],
  ];

  // Live overstemt het target de rest, dus verder niets.
  if (targetHit && !catchUp) return [targetGroup];

  const sales = catchUp ? [] : salesBetween(before, now);
  const climber = biggestClimber(before, now);
  const groups = [];

  sales.forEach((sale, index) => {
    // Gaat de grootste van deze ronde over het record heen? Dan het
    // vuurwerk, met het oude record erbij zodat je ziet wát er gebroken is.
    const isRecord =
      index === 0 &&
      record &&
      sale.amount > record.amount &&
      sale.amount >= recordMinAmount;

    groups.push(
      saleGroup(sale, {
        record: isRecord ? record : null,
        climber: climber?.name === sale.name ? climber : null,
      }),
    );
  });

  // De stijger die niet bij een verkoop hierboven hoort krijgt een eigen
  // kaartje.
  if (climber && !sales.some((sale) => sale.name === climber.name)) {
    const group = [["rank-up", climber]];
    if (climber.position <= 3) group.push(["podium-bounce", {}]);
    groups.push(group);
  }

  if (targetHit && catchUp) groups.push(targetGroup);

  // Er is geld bij gekomen maar niemand sprong eruit: dan alleen de meter.
  // Niet bij het inhalen, daar zegt de kaart het al.
  if (!catchUp && groups.length === 0 && now.amount > before.amount) {
    groups.push([["gauge-pulse", {}]]);
  }

  return groups;
}

// Dezelfde vergelijking, maar los te gebruiken: het animatielab past een
// verzonnen verkoop toe op zijn cijfers en vraagt hier wat er dan zou
// gebeuren. Zo test je in het lab precies wat er live draait.
export function diffGroups(beforeData, nowData, record = readRecord()) {
  return toGroups(snapshot(beforeData), snapshot(nowData), { record });
}

// De grootste losse verkoop van deze ronde, of null.
function biggestSale(before, now) {
  let best = null;
  now.totals.forEach((price, name) => {
    const was = before.totals.get(name) ?? 0;
    const amount = price - was;
    if (amount > 0 && (!best || amount > best.amount)) best = { name, amount };
  });
  return best;
}

// scope: verandert die (andere maand), dan begint het vergelijken opnieuw.
// Zonder dat zou het overschakelen naar een andere maand een regen aan
// "nieuwe verkopen" opleveren, terwijl er niets is gebeurd.
export function useSaleEvents(data, playGroup, scope) {
  const previous = useRef(null);
  const scopeRef = useRef(scope);
  // Het record loopt dóór over maanden heen, dus die zetten we niet terug
  // als de scope wisselt.
  const record = useRef(readRecord());

  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      previous.current = null;
    }

    if (!data) {
      previous.current = null;
      return undefined;
    }

    const now = { ...snapshot(data), at: Date.now() };

    // Na een herstart weten wij niets meer, maar de tv nog wel: de stand van
    // vlak voor hij uitging staat in localStorage. Die geldt dan als vorige
    // meting. Staat daar niets (eerste keer, of een andere maand), dan is
    // dit gewoon de nulmeting zoals altijd.
    const stored = previous.current ? null : readLastSeen(scope);
    const before = previous.current || (stored ? unpack(stored) : null);
    previous.current = now;
    writeLastSeen(pack(now, scope));
    if (!before) return undefined;

    // Zat er lang tussen de vorige meting en deze - de Pi uit, of een tijd
    // geen netwerk - dan is dit inhalen: één kaart "Verkopen gemist" met
    // daarop wie wat verkocht heeft (grootste eerst), en daarna alleen nog
    // de stijger en het target. Met keep, want dit hoort er hoe dan ook te
    // komen, ook als de rij vol zit.
    if (now.at - before.at >= catchUpMinOfflineMs) {
      const sales = salesBetween(before, now);
      if (sales.length > 0) {
        const amount = sales.reduce((sum, sale) => sum + sale.amount, 0);
        playGroup([["missed-sales", { since: before.at, amount, sales }]], {
          keep: true,
        });
      }
      toGroups(before, now, { catchUp: true }).forEach((group) =>
        playGroup(group, { keep: true }),
      );
      return undefined;
    }

    const groups = toGroups(before, now, { record: record.current });

    // Record bijwerken, ook als er geen animatie bij hoort: kennen we er nog
    // geen, dan is deze verkoop vanaf nu de lat.
    const biggest = biggestSale(before, now);
    if (biggest && biggest.amount > (record.current?.amount ?? 0)) {
      record.current = { ...biggest, at: new Date().toISOString() };
      writeRecord(record.current);
    }

    // Elke verkoop als eigen groepje: de animatiemotor speelt ze na elkaar
    // en zet een volgende verkoop in de rij tot het lopende groepje voorbij
    // is.
    groups.forEach((group) => playGroup(group));

    return undefined;
  }, [data, playGroup, scope]);
}
